import _ from 'lodash';
import Debug from '@/util/debug';
import Loading from '@/components/loading/Loading.vue';
import markdown from '@/components/markdown/Markdown.vue';
import prefilled from '@/util/prefill';
import { GOAL_REACH_AND_CLICK, GOAL_LEAD_GENERATION, GOAL_LOCAL_OFFERS, GOAL_REACH_FREQUENCY, GOAL_POST_ENGAGEMENT } from '@/services/GoalTypeService';

const debug = Debug('campaign-recommendation');
const EVENT_START = 'startDraftCreation';

const getTranslationField = (item, locale, key = 'name') => _.get(item, `translations.${locale}.${key}`, item[key]);

const ItemTemplate = {
  dependencies: [
    'localeService',
  ],
  props: { item: { required: true } },
  data() {
    return {
      userLanguage: null,
    };
  },
  computed: {
    name: function computedName() {
      const reg = new RegExp(this.$parent.searchText, 'gi');
      const translated = getTranslationField(this.item, this.userLanguage, 'name');
      const formatted = (translated || '').replace(reg, s => `<span class="highlight">${s}</span>`);
      return formatted;
    },
  },
  template: '<div v-html="name"></div>',
  methods: {
    setLanguage(lang) {
      this.userLanguage = lang;
    },
  },
  mounted() {
    this.localeService.getUserLanguage().then(this.setLanguage);
    this.localeService.getEventBus().$on('language-changed', this.setLanguage);
  },
  beforeDestroy() {
    this.localeService.getEventBus().$off('language-changed', this.setLanguage);
  },
};

export default {
  dependencies: [
    'adApiService',
    'goalTypeService',
    'localeService',
  ],
  components: {
    markdown,
    loading: Loading,
  },
  props: {
    campaign: Object,
    minLength: {
      type: Number,
      default: 1,
    },
    debounce: {
      type: Number,
      default: 300,
    },
    marketingGoals: {
      type: Array,
      default: [],
    },
    hideNonMathingGoals: Boolean,
  },
  data() {
    return {
      startCategoryLoading: false,
      goalTypesLoading: true,
      suggestedGoalLoading: false,

      goalTypes: [],
      categoryItems: [],
      allCategories: null,
      selectedMarketingGoal: null,
      selectedCategory: null,
      suggestedGoal: null,

      itemTemplate: ItemTemplate,
      displayOtherGoals: false,
      preventSetGoalType: false,
      categoryLoading: false,
      displayNotificationFor: null,

      userLanguage: null,
    };
  },
  computed: {
    loading() {
      return (this.startCategoryLoading || this.goalTypesLoading);
    },
    customerRemoteId() {
      if (prefilled && prefilled.data && typeof prefilled.data['customer.remote_id'] !== 'undefined') {
        return prefilled.data['customer.remote_id'];
      }
      return null;
    },
    leadPrivacyPolicyLinkRequired() {
      if (
        _.get(this.goalTypes, `${GOAL_LEAD_GENERATION}.config.custom_privacy_policy_link`, false)
      ) {
        return true;
      }
      return false;
    },
    visibleMarketingGoals() {
      return this.marketingGoals
        .filter(i => {
          if (!this.hideNonMathingGoals) {
            return true;
          }

          return this.selectedCategory && this.selectedCategory.marketing_goals.includes(i.slug);
        });
    },
    dropdownOptions() {
      return (this.allCategories || []).map(r => ({
        value: r,
        name: getTranslationField(r, this.userLanguage, 'name'),
        disabled: r.disabled,
      }));
    },
  },
  methods: {
    start() {
      this.$emit(EVENT_START);
    },
    toggleSeeOtherOptions() {
      this.preventSetGoalType = true;
      this.displayOtherGoals = !this.displayOtherGoals;
      this.displayNotificationFor = null;
      setTimeout(() => { this.preventSetGoalType = false; }, 500);
    },
    updateGoalType(goalType) {
      if (this.preventSetGoalType === false) {
        if (this.getGoalTypeNotice(goalType) && this.displayNotificationFor !== goalType) {
          this.displayNotificationFor = goalType;
          return;
        }

        this.campaign.goal_type = goalType;
        if (this.$refs.campaignRecomendationModal) {
          this.$refs.campaignRecomendationModal.hide();
        }
      }
    },
    getCategoryLabel(item) {
      this.displayOtherGoals = false;
      return getTranslationField(item, this.userLanguage, 'name');
    },
    onUpdateCategoryItems(search) {
      debug.info('onUpdateItems: %s', search);
      this.categoryLoading = true;
      const locale = this.userLanguage;
      this.adApiService.autocompleteBusinessCategories(search, 10, locale).then(res => {
        this.categoryItems = res;
        this.categoryLoading = false;
      });
    },
    onChangeCategory() {
      this.categoryItems = [];
      this.loadSuggestedGoalType();
    },
    clearCategoryAutocomplete() {
      this.$refs.autocomplete.searchText = '';
      this.selectedCategory = null;
      this.campaign.business_category_id = null;
      this.categoryItems = [];
      this.$refs.autocomplete.$el.querySelector('input').focus();
      this.loadSuggestedGoalType();
    },
    updateMarketingGoal(goal) {
      this.selectedMarketingGoal = goal;
      this.displayOtherGoals = false;
      this.loadSuggestedGoalType();
    },
    loadSuggestedGoalType() {
      if (this.selectedCategory !== null && this.selectedMarketingGoal !== null) {
        this.suggestedGoalLoading = true;
        this.adApiService.getRecommendation(this.selectedCategory.id, this.selectedMarketingGoal.id)
          .then(res => {
            let goalType = Object.keys(this.goalTypes)[0];
            if (typeof this.goalTypes[res] !== 'undefined') {
              goalType = res;
            }
            this.suggestedGoal = this.goalTypes[goalType];
            this.suggestedGoal.goal_type = goalType;
            this.suggestedGoalLoading = false;
          });
      } else {
        this.suggestedGoal = null;
      }
      this.campaign.business_category_id = _.get(this, 'selectedCategory.id', null);
      this.displayNotificationFor = null;
    },
    getGoalTypeNotice(goalType) {
      let res = '';
      if (goalType === GOAL_LEAD_GENERATION && this.leadPrivacyPolicyLinkRequired) {
        res = this.$t('pos.setup.notice_leads');
      }
      if (_.get(this.goalTypes, `${goalType}.config.fbPageRequired`, null) === true) {
        if (res !== '') {
          res = `${res}<br/>`;
        }
        res = `${res}${this.$t('pos.setup.notice_fb_required')}`;
      }
      return res;
    },
    hideGoalTypeNotice() {
      this.preventSetGoalType = true;
      this.displayNotificationFor = null;
      setTimeout(() => { this.preventSetGoalType = false; }, 500);
    },
    setLanguage(lang) {
      this.userLanguage = lang;
    },
  },
  beforeDestroy() {
    if (this.$refs.campaignRecomendationModal) {
      this.$refs.campaignRecomendationModal.hide();
    }
    this.localeService.getEventBus().$off('language-changed', this.setLanguage);
  },
  mounted() {
    if (this.$route.params.id === 'new') {
      this.$refs.campaignRecomendationModal.show();
    }

    if (this.campaign.business_category_id !== null) {
      this.startCategoryLoading = true;
      this.adApiService.getBusinessCategory(this.campaign.business_category_id)
        .then(res => {
          this.selectedCategory = res;
          this.startCategoryLoading = false;
        });
    }

    this.localeService.getUserLanguage().then(this.setLanguage);
    this.localeService.getEventBus().$on('language-changed', this.setLanguage);

    Promise.all([
      this.goalTypeService.getOptions()
        .then(res => {
          const sorted = {};

          [
            GOAL_REACH_FREQUENCY,
            GOAL_POST_ENGAGEMENT,
            GOAL_REACH_AND_CLICK,
            GOAL_LEAD_GENERATION,
            GOAL_LOCAL_OFFERS,
          ]
            .filter(key => Object.prototype.hasOwnProperty.call(res, key))
            .forEach(key => sorted[key] = res[key]);

          return sorted;
        })
        .then(res => {
          this.goalTypes = res;
          this.goalTypesLoading = false;
        }),
      this.adApiService.getBusinessCategories()
        .then(res => {
          if (res) {
            res.unshift({ id: null, name: this.$t('pos.campaign-recommendation.category'), disabled: true });
            this.allCategories = res;
          }
        }),
    ])
    .catch(e => {
      debug.info('finished loading. error: %o', e);
      this.goalTypesLoading = false;
    });
  },
};



// WEBPACK FOOTER //
// ./src/components/campaign-recommendation/CampaignRecommendation.js